import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Col, Row } from 'react-flexbox-grid';
import moment from 'moment';

import { AnnouncementDto, AnnouncementTranslationDto } from 'api/announcements/models/AnnouncementDto';
import { AnnouncementStyle } from 'api/announcements/models/AnnouncementStyle';
import AnnouncementsService from 'api/announcements/AnnouncementsService';
import LanguagesService from 'api/languages/LanguagesService';
import { LanguageDto } from 'api/languages/models/LanguageDto';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';

import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import InputGroup from 'common/components/inputGroup/InputGroup';
import SelectController from 'common/components/select/SelectController';
import ColorPickerController from 'common/components/colorPicker/ColorPickerController';
import DateWithHourController from 'common/components/dateWithHour/DateWithHourController';
import LanguagesTab from 'common/components/languagesTabs/LanguagesTab';
import ContentManagement from 'common/components/contentManagement/ContentManagement';
import { FaCalendarAlt } from 'react-icons/fa';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Box from 'common/components/box/Box';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { DATE_FORMAT_DEFAUT, LOGGER_LOG_TYPE } from 'Config';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { resolveAnnouncementColors } from 'common/components/announcements/announcementStyleColors';
import { sanitizeInline } from 'common/components/announcements/sanitizeInline';
import { applyDateToken, DATE_TOKEN } from 'common/components/announcements/applyDateToken';
import styles from './AnnouncementDetailsScreen.module.scss';

type Props = {
};

const emptyTranslation = (lang: LanguageDto): AnnouncementTranslationDto => ({ languageId: lang.id, iso: lang.iso, title: '', message: '' });

const AnnouncementDetailsScreen: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    // eslint-disable-next-line
    let { type, itemId } = useParams<{ type: string, itemId: string }>();

    const [announcement, setAnnouncement] = useState<AnnouncementDto>();
    const [isDetails, setIsDetails] = useState<boolean>(true);
    const [dialogDeleteIsOpen, setDialogDeleteIsOpen] = useState<boolean>(false);

    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageDto | null>(null);
    const [translations, setTranslations] = useState<AnnouncementTranslationDto[]>([]);

    const form = useForm<AnnouncementDto>({
        shouldUnregister: false,
        defaultValues: { style: AnnouncementStyle.WARNING }
    });
    const { handleSubmit, errors, reset, watch, setValue } = form;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMPANIES_WRITE']);

    const styleOptions: SelectValueLabel[] = [
        { value: AnnouncementStyle.SUCCESS, label: t('announcements.styles.success') },
        { value: AnnouncementStyle.WARNING, label: t('announcements.styles.warning') },
        { value: AnnouncementStyle.DANGER, label: t('announcements.styles.danger') },
        { value: AnnouncementStyle.CUSTOM, label: t('announcements.styles.custom') },
    ];

    const selectedStyle = watch('style');
    const watchedTextColor = watch('textColor');
    const watchedBackgroundColor = watch('backgroundColor');
    const watchedEventAt = watch('eventAt');

    const getTranslation = (languageId?: string): AnnouncementTranslationDto =>
        translations.find(tr => tr.languageId === languageId) || { languageId: languageId || '', title: '', message: '' };

    const updateTranslation = (languageId: string, field: 'title' | 'message', value: string) => {
        setTranslations(prev => prev.map(tr => tr.languageId === languageId ? { ...tr, [field]: value } : tr));
    };

    const insertDateToken = (languageId: string) => {
        setTranslations(prev => prev.map(tr => tr.languageId === languageId ? { ...tr, message: `${tr.message || ''}${DATE_TOKEN}` } : tr));
    };

    const copyLanguage = (fromLanguageId: string) => {
        if (!selectedLanguage) {
            return;
        }
        const source = translations.find(tr => tr.languageId === fromLanguageId);
        if (!source) {
            return;
        }
        setTranslations(prev => prev.map(tr => tr.languageId === selectedLanguage.id ? { ...tr, title: source.title, message: source.message } : tr));
    };

    const init = async () => {
        Loading.show();
        try {
            const langs = await LanguagesService.getAll();
            setLanguages(langs);
            const defaultIndex = Math.max(0, langs.findIndex(l => l.isDefault));
            setSelectedLanguageIndex(defaultIndex);
            setSelectedLanguage(langs[defaultIndex] || langs[0] || null);

            if (itemId) {
                const result = await AnnouncementsService.getById(itemId);
                // "copy" pre-fills the form from an existing announcement but saves as a NEW one.
                setAnnouncement(type === 'copy' ? undefined : result);
                reset({
                    style: result.style,
                    textColor: result.textColor,
                    backgroundColor: result.backgroundColor,
                    startsAt: result.startsAt ? new Date(result.startsAt) : undefined as any,
                    endsAt: result.endsAt ? new Date(result.endsAt) : undefined as any,
                    eventAt: result.eventAt ? new Date(result.eventAt) : null,
                });
                setTranslations(langs.map(l => {
                    const existing = result.translations?.find(tr => tr.languageId === l.id);
                    return { languageId: l.id, iso: l.iso, title: existing?.title || '', message: existing?.message || '' };
                }));
            } else {
                setTranslations(langs.map(emptyTranslation));
            }
            Loading.hide();
        } catch (error) {
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load announcement`, error);
            Loading.hide();
        }
    };

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        setIsDetails(type === 'details');
    }, [type]);

    const onLanguageChange = (lang: LanguageDto, index: number) => {
        setSelectedLanguage(lang);
        setSelectedLanguageIndex(index);
    };

    const onSubmit = async (formData: AnnouncementDto) => {
        try {
            const filledTranslations = translations.filter(tr => tr.title?.trim() && tr.message?.trim());

            if (!formData.startsAt || !formData.endsAt) {
                addToast(t('common.messages.complete_required_fields'), { appearance: 'warning' });
                return;
            }
            if (!filledTranslations.length) {
                addToast(t('announcements.messages.translation_required'), { appearance: 'warning' });
                return;
            }
            if (moment(formData.endsAt).isSameOrBefore(moment(formData.startsAt))) {
                addToast(t('announcements.messages.invalid_dates'), { appearance: 'warning' });
                return;
            }
            if (formData.style === AnnouncementStyle.CUSTOM && (!formData.textColor || !formData.backgroundColor)) {
                addToast(t('announcements.messages.custom_colors_required'), { appearance: 'warning' });
                return;
            }

            Loading.show();

            const payload: AnnouncementDto = {
                id: announcement?.id ?? '',
                style: formData.style,
                textColor: formData.style === AnnouncementStyle.CUSTOM ? formData.textColor : null,
                backgroundColor: formData.style === AnnouncementStyle.CUSTOM ? formData.backgroundColor : null,
                startsAt: formData.startsAt,
                endsAt: formData.endsAt,
                eventAt: formData.eventAt ?? null,
                translations: filledTranslations,
            };

            if (announcement != null) {
                await AnnouncementsService.update(payload);
            } else {
                await AnnouncementsService.create(payload);
            }

            Loading.hide();
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.push('/avisos');
        } catch (error) {
            Loading.hide();
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't save announcement`, error);
        }
    };

    const removeItem = async () => {
        setDialogDeleteIsOpen(false);
        if (!announcement) {
            return;
        }
        try {
            Loading.show();
            await AnnouncementsService.remove(announcement);
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            history.push('/avisos');
            Loading.hide();
        } catch (error) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const previewColors = resolveAnnouncementColors(selectedStyle as AnnouncementStyle, watchedTextColor, watchedBackgroundColor);
    const previewTranslation = getTranslation(selectedLanguage?.id);
    const displayTitle = translations.find(tr => tr.title)?.title ?? '';

    return (
        <ScreenTitle title={t('announcements.title')}>
            <CompactPageLayout title={t('announcements.title') as string} onBack={() => history.push('/avisos')}>
                <div className={styles.contentWrapper}>
                    <Box>
                        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                            <Row>
                                <Col xs={12} md={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('announcements.list.starts_at')} {!isDetails ? '*' : ''}</Label>
                                        <DateWithHourController
                                            form={form as any}
                                            name="startsAt"
                                            datePlaceholder={t('common.date')}
                                            timePlaceholder={t('common.hour')}
                                            dateFormat={DATE_FORMAT_DEFAUT}
                                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} isClearable={true} onClear={() => setValue('startsAt', null as any)} />}
                                            isDisabled={isDetails}
                                            rules={{ required: true }}
                                        />
                                        <InputError error={(errors as any).startsAt} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('announcements.list.ends_at')} {!isDetails ? '*' : ''}</Label>
                                        <DateWithHourController
                                            form={form as any}
                                            name="endsAt"
                                            datePlaceholder={t('common.date')}
                                            timePlaceholder={t('common.hour')}
                                            dateFormat={DATE_FORMAT_DEFAUT}
                                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} isClearable={true} onClear={() => setValue('endsAt', null as any)} />}
                                            isDisabled={isDetails}
                                            rules={{ required: true }}
                                            minDate={watch('startsAt') ? moment(watch('startsAt')).toDate() : undefined}
                                        />
                                        <InputError error={(errors as any).endsAt} />
                                    </FormItem>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12} md={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('announcements.list.style')}</Label>
                                        <SelectController
                                            name="style"
                                            form={form as any}
                                            isDisabled={isDetails}
                                            options={styleOptions}
                                            placeholder={t('announcements.list.style')}
                                        />
                                    </FormItem>
                                </Col>
                                {selectedStyle === AnnouncementStyle.CUSTOM &&
                                    <>
                                        <Col xs={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('announcements.form.text_color')} {!isDetails ? '*' : ''}</Label>
                                                <ColorPickerController name="textColor" form={form as any} disabled={isDetails} />
                                            </FormItem>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('announcements.form.background_color')} {!isDetails ? '*' : ''}</Label>
                                                <ColorPickerController name="backgroundColor" form={form as any} disabled={isDetails} />
                                            </FormItem>
                                        </Col>
                                    </>
                                }
                            </Row>

                            <Row>
                                <Col xs={12} md={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('announcements.form.event_at')}</Label>
                                        <DateWithHourController
                                            form={form as any}
                                            name="eventAt"
                                            datePlaceholder={t('common.date')}
                                            timePlaceholder={t('common.hour')}
                                            dateFormat={DATE_FORMAT_DEFAUT}
                                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} isClearable={true} onClear={() => setValue('eventAt', null as any)} />}
                                            isDisabled={isDetails}
                                        />
                                        <span className={styles.hint}>{t('announcements.form.event_at_hint')}</span>
                                    </FormItem>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <Label className={styles.label}>{t('announcements.form.content')} {!isDetails ? '*' : ''}</Label>
                                    <LanguagesTab
                                        selectedLanguageIndex={selectedLanguageIndex}
                                        languages={languages}
                                        onChange={onLanguageChange}
                                    >
                                        {languages.map((lang, index) => {
                                            const tr = getTranslation(lang.id);
                                            return (
                                                <div key={lang.id} className={index !== selectedLanguageIndex ? styles.hideTab : ''}>
                                                    {!isDetails &&
                                                        <div className={styles.contentManagement}>
                                                            <ContentManagement
                                                                options={languages.filter(x => x.id !== lang.id).map(x => ({ label: x.name, value: x.id }))}
                                                                onCopy={fromId => copyLanguage(fromId)}
                                                                isInnerContainer={true}
                                                            />
                                                        </div>
                                                    }
                                                    <FormItem>
                                                        <Label className={styles.label}>{t('announcements.list.title')}</Label>
                                                        <InputGroup
                                                            value={tr.title}
                                                            placeholder={t('announcements.list.title')}
                                                            disabled={isDetails}
                                                            maxLength={250}
                                                            hideMessageLength={true}
                                                            onValueChange={value => updateTranslation(lang.id, 'title', String(value ?? ''))}
                                                        />
                                                    </FormItem>
                                                    <FormItem>
                                                        <div className={styles.messageHeader}>
                                                            <Label className={styles.label}>{t('announcements.list.message')}</Label>
                                                            {!isDetails &&
                                                                <button type="button" className={styles.tokenButton} onClick={() => insertDateToken(lang.id)}>
                                                                    {t('announcements.form.insert_datetime')}
                                                                </button>
                                                            }
                                                        </div>
                                                        <div className={styles.textArea}>
                                                            <textarea
                                                                value={tr.message}
                                                                disabled={isDetails}
                                                                placeholder={t('announcements.list.message')}
                                                                onChange={e => updateTranslation(lang.id, 'message', e.target.value)}
                                                            />
                                                        </div>
                                                    </FormItem>
                                                </div>
                                            );
                                        })}
                                    </LanguagesTab>
                                    <span className={styles.hint}>{t('announcements.form.message_hint')}</span>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <Label className={styles.label}>{t('announcements.form.preview')}</Label>
                                    <div className={styles.preview} style={{ color: previewColors.textColor, backgroundColor: previewColors.backgroundColor }}>
                                        <strong className={styles.previewTitle}>{previewTranslation.title || t('announcements.form.preview_title_placeholder')}</strong>
                                        <span dangerouslySetInnerHTML={{ __html: sanitizeInline(applyDateToken(previewTranslation.message, watchedEventAt as any)) || t('announcements.form.preview_message_placeholder') }} />
                                    </div>
                                </Col>
                            </Row>

                            <div className={styles.buttonContainer}>
                                <Button preset={'secondary'} type='button' text={t('common.cancel')} onClick={() => history.push('/avisos')} />
                                {type === 'details' && hasWritePolicy &&
                                    <Button type="button" text={t('common.remove')} preset={'danger'} onClick={() => setDialogDeleteIsOpen(true)} />
                                }
                                {type === 'details' && hasWritePolicy &&
                                    <Button type='button' text={t('common.edit')} onClick={() => history.push(`/avisos/edit/${itemId}`)} />
                                }
                                {type !== 'details' && hasWritePolicy &&
                                    <Button type='submit' text={t('common.save')} />
                                }
                                <QuestionYesNo
                                    message={t('common.messages.remove_record_with_ident', { name: displayTitle })}
                                    isVisible={dialogDeleteIsOpen}
                                    onYes={() => removeItem()}
                                    onNo={() => setDialogDeleteIsOpen(false)} />
                            </div>
                        </form>
                    </Box>
                </div>
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default AnnouncementDetailsScreen;
